/**
 * Settings Page Component
 * Manage application settings including AI configuration, ADAM integration, etc.
 */

import React, { useState, useEffect } from 'react';
import {
  Box, Paper, Typography, Button, Alert, CircularProgress,
  Tabs, Tab, TextField, FormControlLabel, Switch, Divider,
  Stack, Breadcrumbs, Link as MuiLink
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import SaveIcon from '@mui/icons-material/Save';
import SettingsIcon from '@mui/icons-material/Settings';
import { useAuthenticatedRequest } from '../../hooks/useAuthenticatedRequest';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const SettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { request, isReady } = useAuthenticatedRequest();
  const [currentTab, setCurrentTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // ADAM Configuration
  const [adamAuthEnabled, setAdamAuthEnabled] = useState(false);
  const [adamApiToken, setAdamApiToken] = useState('');
  const [adamBaseUrl, setAdamBaseUrl] = useState('');
  const [adamFallbackEnabled, setAdamFallbackEnabled] = useState(true);
  const [adamTimeout, setAdamTimeout] = useState('10');
  const [emergencyAdminBypass, setEmergencyAdminBypass] = useState(false);

  // Database Configuration (read-only display)
  const [dbName, setDbName] = useState('');
  const [dbUser, setDbUser] = useState('');
  const [dbHost, setDbHost] = useState('');
  const [dbPort, setDbPort] = useState('');

  // Django Configuration (read-only display)
  const [debugMode, setDebugMode] = useState(false);
  const [allowedHosts, setAllowedHosts] = useState('');

  useEffect(() => {
    if (isReady) {
      fetchSettings();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReady]);

  const fetchSettings = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await request(() => axios.get('/api/settings/'));
      const data = response.data;

      // ADAM Configuration
      setAdamAuthEnabled(data.adam_configuration.adam_auth_enabled === 'true');
      setAdamApiToken(data.adam_configuration.adam_api_token || '');
      setAdamBaseUrl(data.adam_configuration.adam_base_url || '');
      setAdamFallbackEnabled(data.adam_configuration.adam_fallback_enabled === 'true');
      setAdamTimeout(data.adam_configuration.adam_timeout || '10');
      setEmergencyAdminBypass(data.adam_configuration.emergency_admin_bypass === 'true');

      // Database Configuration (optional - may not be in response)
      if (data.database_configuration) {
        setDbName(data.database_configuration.db_name || '');
        setDbUser(data.database_configuration.db_user || '');
        setDbHost(data.database_configuration.db_host || '');
        setDbPort(data.database_configuration.db_port || '');
      }

      // Django Configuration (optional - may not be in response)
      if (data.django_configuration) {
        setDebugMode(data.django_configuration.debug === 'True');
        setAllowedHosts(data.django_configuration.allowed_hosts || '');
      }
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccessMessage(null);
    try {
      const settingsData = {
        ADAM_AUTH_ENABLED: adamAuthEnabled.toString(),
        ADAM_API_TOKEN: adamApiToken,
        ADAM_BASE_URL: adamBaseUrl,
        ADAM_FALLBACK_ENABLED: adamFallbackEnabled.toString(),
        ADAM_TIMEOUT: adamTimeout,
        EMERGENCY_ADMIN_BYPASS: emergencyAdminBypass.toString(),
      };

      const response = await request(() => axios.post('/api/settings/', settingsData));
      setSuccessMessage(response.data.message);

      // Scroll to top to show success message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to save settings');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSaving(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setCurrentTab(newValue);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Breadcrumbs sx={{ mb: 2 }}>
        <MuiLink
          component="button"
          onClick={() => navigate('/')}
          sx={{ cursor: 'pointer', textDecoration: 'none' }}
          color="inherit"
        >
          Dashboard
        </MuiLink>
        <Typography color="text.primary">Settings</Typography>
      </Breadcrumbs>

      <Box display="flex" alignItems="center" gap={1} mb={3}>
        <SettingsIcon />
        <Typography variant="h4">Settings</Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      <Paper sx={{ width: '100%' }}>
        <Tabs
          value={currentTab}
          onChange={handleTabChange}
          aria-label="settings tabs"
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          <Tab label="ADAM Integration" />
          <Tab label="Database" />
          <Tab label="Advanced" />
        </Tabs>

        <TabPanel value={currentTab} index={0}>
          <Typography variant="h6" gutterBottom>
            ADAM External Authentication
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Configure integration with ADAM external authentication system.
          </Typography>

          <Stack spacing={3}>
            <FormControlLabel
              control={
                <Switch
                  checked={adamAuthEnabled}
                  onChange={(e) => setAdamAuthEnabled(e.target.checked)}
                />
              }
              label="Enable ADAM Authentication"
            />

            <TextField
              label="ADAM API Token"
              value={adamApiToken}
              onChange={(e) => setAdamApiToken(e.target.value)}
              fullWidth
              type="password"
              disabled={!adamAuthEnabled}
              helperText="Authentication token for ADAM API"
            />

            <TextField
              label="ADAM Base URL"
              value={adamBaseUrl}
              onChange={(e) => setAdamBaseUrl(e.target.value)}
              fullWidth
              disabled={!adamAuthEnabled}
              helperText="Base URL for ADAM service"
            />

            <TextField
              label="Request Timeout (seconds)"
              value={adamTimeout}
              onChange={(e) => setAdamTimeout(e.target.value)}
              fullWidth
              type="number"
              disabled={!adamAuthEnabled}
              helperText="Timeout for ADAM API requests"
            />

            <FormControlLabel
              control={
                <Switch
                  checked={adamFallbackEnabled}
                  onChange={(e) => setAdamFallbackEnabled(e.target.checked)}
                  disabled={!adamAuthEnabled}
                />
              }
              label="Enable Local Fallback"
            />

            <Divider />

            <FormControlLabel
              control={
                <Switch
                  checked={emergencyAdminBypass}
                  onChange={(e) => setEmergencyAdminBypass(e.target.checked)}
                  color="error"
                />
              }
              label="Emergency Admin Bypass (DANGEROUS)"
            />
            <Alert severity="warning">
              Emergency admin bypass should only be enabled in development or emergency situations.
              This allows admin login even when ADAM authentication fails.
            </Alert>
          </Stack>
        </TabPanel>

        <TabPanel value={currentTab} index={1}>
          <Typography variant="h6" gutterBottom>
            Database Configuration
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Database settings (read-only, configure via docker-compose.yml)
          </Typography>

          <Stack spacing={3}>
            <TextField
              label="Database Name"
              value={dbName}
              fullWidth
              disabled
              helperText="Database name"
            />

            <TextField
              label="Database User"
              value={dbUser}
              fullWidth
              disabled
              helperText="Database username"
            />

            <TextField
              label="Database Host"
              value={dbHost}
              fullWidth
              disabled
              helperText="Database host (container name or IP)"
            />

            <TextField
              label="Database Port"
              value={dbPort}
              fullWidth
              disabled
              helperText="Database port"
            />
          </Stack>
        </TabPanel>

        <TabPanel value={currentTab} index={2}>
          <Typography variant="h6" gutterBottom>
            Advanced Django Settings
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Advanced Django configuration (read-only, configure via docker-compose.yml)
          </Typography>

          <Stack spacing={3}>
            <FormControlLabel
              control={<Switch checked={debugMode} disabled />}
              label="Debug Mode"
            />

            <TextField
              label="Allowed Hosts"
              value={allowedHosts}
              fullWidth
              disabled
              helperText="Comma-separated list of allowed hosts"
            />

            <Alert severity="info">
              To modify database and Django settings, update docker-compose.yml and restart the containers.
            </Alert>
          </Stack>
        </TabPanel>

        <Box sx={{ p: 3, borderTop: 1, borderColor: 'divider' }}>
          <Stack direction="row" spacing={2} justifyContent="flex-end">
            <Button
              variant="outlined"
              onClick={() => navigate('/')}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={20} /> : <SaveIcon />}
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? 'Saving...' : 'Save Settings'}
            </Button>
          </Stack>
        </Box>
      </Paper>
    </Box>
  );
};

export default SettingsPage;
